import camelcaseKeys from 'camelcase-keys';
import { defineStore } from 'pinia';
import CommercialGoalsAPI from 'dashboard/api/commercialGoals';

const camelize = data => camelcaseKeys(data || {}, { deep: true });

// Erros 422 trazem `error` (mensagem traduzida) e `conflict` (meta que causou a
// sobreposição). Sem mensagem do servidor, `error.message` fica vazio e a UI usa o texto genérico.
const throwGoalError = error => {
  const { error: message, conflict } = error.response?.data || {};
  const goalError = new Error(message || '');
  goalError.conflict = conflict ? camelize(conflict) : null;
  throw goalError;
};

export const useCommercialGoalsStore = defineStore('commercialGoals', {
  state: () => ({
    records: [],
    meta: { count: 0, currentPage: 1, perPage: 25 },
    progress: [],
    myProgress: [],
    uiFlags: {
      fetchingList: false,
      fetchingProgress: false,
      fetchingMyProgress: false,
      creatingItem: false,
      updatingItem: false,
      deletingItem: false,
    },
  }),

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    async get(params) {
      this.setUIFlag({ fetchingList: true });
      try {
        const { data } = await CommercialGoalsAPI.list(params);
        this.records = camelize(data.payload);
        this.meta = camelize(data.meta);
        return this.records;
      } catch (error) {
        return throwGoalError(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async create(attrs) {
      this.setUIFlag({ creatingItem: true });
      try {
        const { data } = await CommercialGoalsAPI.create(attrs);
        return camelize(data.payload);
      } catch (error) {
        return throwGoalError(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    async update({ id, ...attrs }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const { data } = await CommercialGoalsAPI.update(id, attrs);
        const goal = camelize(data.payload);
        this.records = this.records.map(r => (r.id === goal.id ? goal : r));
        return goal;
      } catch (error) {
        return throwGoalError(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deletingItem: true });
      try {
        await CommercialGoalsAPI.delete(id);
        this.records = this.records.filter(r => r.id !== id);
        return id;
      } catch (error) {
        return throwGoalError(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },

    async fetchProgress() {
      this.setUIFlag({ fetchingProgress: true });
      try {
        const { data } = await CommercialGoalsAPI.getProgress();
        this.progress = camelize(data.payload);
        return this.progress;
      } catch (error) {
        return throwGoalError(error);
      } finally {
        this.setUIFlag({ fetchingProgress: false });
      }
    },

    async fetchMyProgress() {
      this.setUIFlag({ fetchingMyProgress: true });
      try {
        const { data } = await CommercialGoalsAPI.getMyProgress();
        this.myProgress = camelize(data.payload);
        return this.myProgress;
      } catch (error) {
        return throwGoalError(error);
      } finally {
        this.setUIFlag({ fetchingMyProgress: false });
      }
    },
  },
});
